import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Popup, Icon } from "semantic-ui-react";
import { Addons, HotelAddonInventory, Hotels } from "../../api/services/next";
import { onUpdateGridHandler } from '../../components/GridPanel/utility';
import { ChainAddonsDropDown } from "../../components/shared/ChainAddonsDropDown";
import CheckBox from "../../components/shared/Checkbox";
import { MenuActions } from "../../components/shared/constants";
import HotelsDropDown from "../../components/shared/HotelsDropDown";
import { AddButton, IconButton } from '../../components/shared/icons';
import { onError, onSuccess, onWarning } from "../../components/shared/messages";
import { PermissionsActions, UserHasPermission } from '../../components/shared/permissionsActions';
import TextInput from '../../components/shared/TextInput';
import { useLoader } from '../../hooks/useLoader';
import useBoolean from '../../hooks/useBoolean';
import { useConfirm } from "../../hooks/useConfirm";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import { useMessageResources, useTextResources } from '../../language';
import GridSearchTemplate from '../GridSearchTemplate';
import AddMultiHotelAddonInventory from './popups/AddMultiHotelAddonInventory';
import AssignRoomTypesToHotelAddonInventory from './popups/AssignRoomTypesToHotelAddonInventory';
import { use } from "react";

const genericFieldAndValueDropDownStyles = { position: 'absolute', marginTop: '-17px', width: '13%' };
// const defaultInputProps = { search: true, style: genericFieldAndValueDropDownStyles };
const hasPermission = UserHasPermission(PermissionsActions.ViewHotelAddons)
const mergeData = (list1, list2) => {
    const combinedData = [...list1];

    list2.forEach(newItem => {
        const index = combinedData.findIndex(item => item.Id === newItem.Id);
        if (index !== -1) {
            combinedData[index] = newItem; // Update the existing item
        } else {
            combinedData.push(newItem); // Add new item if not found
        }
    });

    return combinedData;
};

export const HotelAddonInventoryPage = () => {
    const currentHotel = useCurrentHotel();

    const [searchSpec, setSearch] = useState({
        HotelIds: [],
        ChainAddonIds: [],
        IsActive: true,
        Includes: [
            "ChainAddon",
            "RoomTypeAddonInventoryMappings"
        ]
    })

    const [data, setData] = useState([])
    const [dataForSave, setDataForSave] = useState([]);
    const [hotels, setHotels] = useState([]);
    const [chainAddons, setChainAddons] = useState([]);

    const [selectedHotelAddonInventory, setSelectedHotelAddonInventory] = useState()

    const hotelSpec = useMemo(() => ({ HotelChainId: currentHotel.HotelChainId }), [currentHotel]);
    const chainAddonsSpec = useMemo(() => ({ HotelChainId: currentHotel.HotelChainId }), [currentHotel.HotelChainId]);

    const doSearch = useRef();
    const message = useMessageResources();
    const loader = useLoader();
    const text = useTextResources();
    const confirm = useConfirm();

    const {
        setTrue: openAddMultipleModal,
        setFalse: closeAddMultipleModal,
        value: isAddMultipleModalOpen
    } = useBoolean(false);

    const {
        setTrue: openRoomTypeMappingModal,
        setFalse: closeRoomTypeMappingModal,
        value: isRoomTypeMappingModal
    } = useBoolean(false);

    useEffect(() => {
        initData();
    }, [hotelSpec, chainAddonsSpec])

    useEffect(() => {
        if (window.optima.commands) {
            window.optima.commands.unsubscribe(MenuActions.save)
            window.optima.commands.subscribe(MenuActions.save, save);
        }
    }, [dataForSave])

    const initData = useCallback(async () => {
        loader(true)
        try {
            await Promise.all([
                getHotels(),
                getChainAddons()
            ])
        } catch (e) {
            onError(e);
        } finally {
            loader(false);
        }
    }, [hotelSpec, chainAddonsSpec])

    const getHotels = useCallback(async () => {
        const hotels = await Hotels.GetHotels(hotelSpec);
        setHotels(hotels);
    }, [hotelSpec])

    const getChainAddons = useCallback(async () => {
        const addons = await Addons.GetHotelChainAddons(chainAddonsSpec);
        setChainAddons(addons);
    }, [chainAddonsSpec])

    const confirmDeletion = useCallback(() => confirm({
        header: message('AreYouSure'),
        content: message('AreYouSureYouWouldLikeToDeleteSelectedItems'),
        confirmButton: {
            content: text('Yes'),
            positive: true,
        },
        cancelButton: {
            content: text('No'),
            negative: true,
        },
    }), []);

    const validate = () => {
        let valid = true;
        if (dataForSave.some(x => !x.HotelId)) {
            valid = false;
            onWarning('ErrorHotelRequired')
        }
        if (dataForSave.some(x => !x.ChainAddonId)) {
            valid = false;
            return onWarning('ErrorAddonRequired')
        }

        return valid;
    }

    const save = async () => {
        if (!dataForSave.length || !validate()) {
            return;
        }

        try {
            loader(true);

            let _data = dataForSave.map(d => isNewRow(d) ? { ...d, Id: undefined } : d);

            const savedData = await HotelAddonInventory.SaveHotelAddonInventory(_data)

            // Merge the saved data with the existing data without duplicates
            const mergedData = mergeData(data, savedData);

            setDataForSave([])
            setData(mergedData);

            onSuccess();
        } catch (e) {
            onError(e?.error?.detail ? e?.error?.detail : e);
        } finally {
            loader(false);
        }
    }

    const deleteRows = async (rowsToDelete) => {
        try {
            loader(true);

            let ids = rowsToDelete
                .map(x => gridData[x])
                .filter(x => !isNewRow(x))
                .map(x => x.Id)

            if (ids?.length) {
                await HotelAddonInventory.DeleteHotelAddonInventory(ids)
            }

            onSuccess();
        } catch (e) {
            onError(e?.error?.detail ? e?.error?.detail : e);
        } finally {
            loader(false);
        }
    }

    const onGridUpdate = async (added, changed, deleted) => {

        let deletedRows = deleted;

        if (deleted?.length > 0) {
            if (await confirmDeletion()) {
                await deleteRows(deleted)
                const idsToDelete = deletedRows.map(index => gridData[index].Id);
                if (idsToDelete?.length) {
                    setDataForSave(prev => prev.filter(x => !idsToDelete.includes(x.Id)))
                    setData(prev => prev.filter(x => !idsToDelete.includes(x.Id)))
                }
            }
            else {
                deletedRows = []
            }
        } else {
            let updated = onUpdateGridHandler(data, added, changed);
            setData(updated)
        }
    }

    const onSearchComplete = (data) => {
        setData(data)
    }

    const createNewRow = () => {
        return {
            Id: `New-${crypto.randomUUID()}`,
        }
    }

    const generalOnChange = (row, field, value) => {

        if (field === "DefaultAmount" || field === "ViewOrder") {
            value = Number(value)
            if (isNaN(value)) {
                onWarning(message("NumbersOnly"))
                return;
            }
        }

        if (row[field] === value) {
            return;
        }

        row[field] = value;

        const id = row.Id;

        setDataForSave(prev => {
            let prevCopy = [...prev];
            const index = prevCopy.findIndex(x => x.Id === id)
            if (index === -1) {
                prevCopy = [...prevCopy, row]
            } else {
                prevCopy[index] = row;
            }
            return prevCopy;
        })

        // row[field] = value;
        // setData([...data])
    }

    const editFormatter = ({ row }) => {
        if (hasPermission) {
            const hasRoomTypes = row?.RoomTypeAddonInventoryMappings?.length > 0;
            const iconColor = hasRoomTypes ? 'green' : undefined;
            
            return (
                <IconButton 
                    content={hasRoomTypes ? 'EditRoomTypes' : 'Edit'} 
                    onClick={() => openRoomTypeMapping(row?.Id)}
                >
                    <Icon link name='pencil' color={iconColor} />
                </IconButton>
            );
        }
        return <></>
    };

    const openAddMultiple = () => {
        openAddMultipleModal();
    }

    const onAddMultipleClose = () => {
        closeAddMultipleModal();
        doSearch.current?.();
    }

    const gridData = useMemo(() => mergeData(data, dataForSave), [data, dataForSave])

    const openRoomTypeMapping = useMemo(() => (id) => {
        const hotelAddonInventory = gridData.find(x => x.Id === id);
        if (!hotelAddonInventory) {
            return
        }

        if (isNewRow(hotelAddonInventory)) {
            onWarning('PleaseSaveRowBeforeEdit')
            return;
        }

        if (!hotelAddonInventory.HotelId) {
            onWarning('ErrorHotelRequired')
            return
        }

        const hotel = hotels.find(hotel => hotel.Id === hotelAddonInventory.HotelId);
        if (!hotelAddonInventory.ChainAddonId) {
            onWarning('ErrorAddonRequired')
            return
        }
        if (!hotelAddonInventory.ChainAddon) {
            hotelAddonInventory.ChainAddon = chainAddons.find(chain => chain.Id === hotelAddonInventory.ChainAddonId);
        }

        setSelectedHotelAddonInventory({ hotelAddonInventory, hotel });
        openRoomTypeMappingModal();
    }, [gridData])

    const onRoomTypeMappingClose = () => {
        closeRoomTypeMappingModal()
        setSelectedHotelAddonInventory(null);
        doSearch.current?.();
    }

    const isNewRow = (row) => {
        return row?.Id && isNaN(row?.Id) && row?.Id.includes('New')
    }

    const gridOptions = useMemo(() => ({
        search: {
            title: text("HotelAddonInventory"),
            fields: [
                <Form.Field
                    label={text('Hotels')}
                    placeholder={text('All')}
                    multiple
                    data={hotels}
                    control={HotelsDropDown}
                    key="HotelIds"
                    clearable
                    allowSelectAll
                />,
                <Form.Field
                    label={text('Addons')}
                    placeholder={text('All')}
                    multiple
                    data={chainAddons}
                    control={ChainAddonsDropDown}
                    key="ChainAddonIds"
                    clearable
                    allowSelectAll />,
            ],
            initial: searchSpec,
            spec: searchSpec,
            // validate: validations,
            method: 'POST',
            api: HotelAddonInventory.GetHotelAddonInventory,
            setDoSearchRef: ref => doSearch.current = ref,
            searchOnLoad: true,
        },
        grid: {
            columns: [
                {
                    name: 'HotelId',
                    title: text("Hotel"),
                    formatter: ({ row }) => {
                        if (!isNewRow(row)) {
                            return hotels?.find(x => x.Id === row?.HotelId)?.Name || "";
                        }
                        return <Form.Field
                            placeholder={text('Hotel')}
                            search
                            control={HotelsDropDown}
                            key="HotelId"
                            data={hotels}
                            value={row?.HotelId}
                            onChange={(value) => {
                                generalOnChange(row, "HotelId", value);
                            }}
                            style={genericFieldAndValueDropDownStyles} />;
                    }
                },
                {
                    name: 'ChainAddonId',
                    title: text("Addons"),
                    formatter: ({ row }) => {
                        if (!isNewRow(row)) {
                            return row?.ChainAddon?.Name || chainAddons?.find(x => x.Id === row?.ChainAddonId)?.Name || "";
                        }
                        return <Form.Field
                            placeholder={text('AddOn')}
                            search
                            control={ChainAddonsDropDown}
                            key="ChainAddonId"
                            value={row?.ChainAddonId}
                            data={chainAddons.filter(ca => !data.some(d => d.ChainAddonId === ca.Id && d.HotelId === row.HotelId))}
                            onChange={(value) => {
                                generalOnChange(row, "ChainAddonId", value);
                            }}
                            style={genericFieldAndValueDropDownStyles} />;
                    }
                },
                {
                    name: "IsInventoryManaged",
                    title: text("IsInventoryManaged"),
                    formatter: ({ value, row }) => <Form.Field control={CheckBox} value={value}
                        onChange={(value) => {
                            generalOnChange(row, "IsInventoryManaged", value);
                        }} />,
                },
                {
                    name: "DefaultAmount",
                    title: text("DefaultAmount"),
                    formatter: ({ value, row }) => <Form.Field
                        control={TextInput}
                        placeholder={text("DefaultAmount")}
                        value={value}
                        onBlur={(value) => {
                            generalOnChange(row, "DefaultAmount", value);
                        }}
                        type='number'
                    />
                },
                {
                    name: "ViewOrder",
                    title: text("DisplayOrder"),
                    formatter: ({ value, row }) => <Form.Field
                        control={TextInput}
                        placeholder={text("DisplayOrder")}
                        value={value || 0}
                        onBlur={(value) => {
                            generalOnChange(row, "ViewOrder", value);
                        }}
                        type='number'
                    />
                },
                {
                    name: 'EditRoomTypes',
                    title: text('EditRoomTypes'),
                    formatter: editFormatter,
                },
            ],
            cacheId: 'HotelAddonInventoryPage',
            paging: true,
            selectable: true,
            showDeleteMultiple: true,
            editable: {
                allowAdd: true,
                allowDelete: true,
                inline: false,
                extraProps: {
                    addedRows: [],
                    onAddedRowsChange: (addedRows) => {
                        if (addedRows.length) {
                            const _addedRows = addedRows.map(createNewRow);
                            setDataForSave(prev => [...prev, ..._addedRows]);
                        }
                    }
                }
            },
            customOptions: {
                sort: {
                    sorting: [
                        { columnName: 'DisplayOrder', direction: 'asc' },
                    ]
                },
                order: {}
            },
            menu: hasPermission ? [
                <Popup
                    content='Add Multiple Hotel Addon Inventory'
                    position='bottom center'
                    mouseEnterDelay={300}
                    key="AddMultiple"
                    trigger={
                        <Icon
                            name='add'
                            link
                            onClick={openAddMultiple}
                        />
                    }
                />
            ] : undefined
        }
    }), [hotels, chainAddons, gridData, searchSpec, currentHotel])

    return (
        <>
            <GridSearchTemplate
                options={gridOptions}
                onSearchChange={setSearch}
                onGridUpdate={onGridUpdate}
                onSearchComplete={onSearchComplete}
                data={gridData}
            />
            {hasPermission && selectedHotelAddonInventory && (
                <AssignRoomTypesToHotelAddonInventory
                    open={isRoomTypeMappingModal}
                    hotel={selectedHotelAddonInventory.hotel}
                    hotelAddonInventory={selectedHotelAddonInventory.hotelAddonInventory}
                    onClose={onRoomTypeMappingClose}
                />
            )}
            {hasPermission && isAddMultipleModalOpen && <AddMultiHotelAddonInventory
                open={isAddMultipleModalOpen}
                onClose={onAddMultipleClose}
            />}
        </>
    )
}